import React, { useCallback, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { MenuHeaderNavigation } from 'features/menu/components/menu-header-navigation';
import { MenuItemUnavailable } from 'features/menu/components/menu-item-unavailable';
import { MenuSection } from 'features/menu/components/menu-section/menu-section';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import { pinTargetItems } from 'features/menu/utils/pin-target-items';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Icon } from 'ucl/components/icon/Icon';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { asPreviewMenuEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/as-preview-menu-entry';
import { useOneMenuCatalog } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-one-menu-catalog';
import { buildCatalogSectionProductNodes } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/build-unavailable-section-recovery-data';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { routes } from 'utils/routing';

type MenuSectionCatalogPreviewProps = {
  sectionId: string;
  targetItemIds?: string[];
  unavailableItemId?: string;
};

/**
 * No-store section deeplink: same section product-list chrome as a normal
 * section page (header + products), without section tabs / other sections.
 * Select-restaurant CTA opens the minimal locator; after store pick,
 * `/menu/section/:id` re-checks availability.
 */
export function MenuSectionCatalogPreview({
  sectionId,
  targetItemIds = [],
  unavailableItemId,
}: MenuSectionCatalogPreviewProps) {
  const router = useRouter();
  const { language } = useLocale();
  const { entries, loading, error } = useOneMenuCatalog();

  const preview = useMemo(() => {
    if (!sectionId || !entries.length) {
      return null;
    }
    const localized = entries.map(entry => asPreviewMenuEntry(entry, language));
    const sectionProducts = buildCatalogSectionProductNodes(localized, sectionId);
    if (!sectionProducts) {
      return null;
    }
    return {
      title: sectionProducts.title,
      products: pinTargetItems(sectionProducts.products, targetItemIds),
    };
  }, [entries, language, sectionId, targetItemIds]);

  const handleSelectRestaurant = useCallback(() => {
    if (!sectionId) {
      return;
    }
    router.navigate({
      pathname: routes.minimalStoreLocator,
      params: {
        redirectUrl: `${routes.menu}/section/${sectionId}`,
        ...(targetItemIds.length ? { targetItemIds: targetItemIds.join(',') } : {}),
        ...(unavailableItemId ? { unavailableItemId } : {}),
      },
    });
  }, [router, sectionId, targetItemIds, unavailableItemId]);

  if (loading) {
    return <MenuSkeletonLoader />;
  }

  if (error || !preview) {
    return <MenuItemUnavailable isOffer={false} />;
  }

  return (
    <SafeAreaView edges={['bottom']} style={styles.container} testID="menu-section-catalog-preview">
      <MenuHeaderNavigation title={preview.title} key={preview.title || sectionId} />
      <View style={styles.list}>
        <MenuSection data={preview.products} hidePrices />
      </View>
      <View style={styles.footer}>
        <ActionButton
          testID="menu-section-catalog-preview-select-restaurant"
          fullWidth
          variant={ActionButtonVariants.OUTLINE}
          onPress={handleSelectRestaurant}
          leftIcon={
            <Icon name="mi:location-on" width="24px" {...hiddenAccessibilityPlatformProps} />
          }
        >
          {t('selectRestaurantToContinue')}
        </ActionButton>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: primitive.$mayoEggWhite,
    flex: 1,
  },
  footer: {
    paddingBottom: spacing.$5,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
    width: '100%',
  },
  list: {
    flex: 1,
    paddingTop: spacing.$4,
  },
});
